import { useEffect, useMemo, useRef, useState } from 'react';

import { VideoResolution } from '../../../types.ts/video';
import { getFormattedDurationForStoryVideo } from '../../../utility/timeFormat';

import type {
  AssociatedVideo,
  StoryVideo,
  VideoAction,
} from '../../../types.ts/story';
import type { AspectRatio } from '../../../types.ts/video';

export type DropdownOption = {
  id: string;
  title: string;
  duration?: number | null;
  videoFilePrimary?: StoryVideo;
  associatedVideos?: AssociatedVideo[];
  aspectRatio?: AspectRatio;
  isHidden?: boolean;
  lastAction?: VideoAction;
  isClientReady?: boolean;
};

interface UseVideoSelectorProps {
  videoCreator: any; // IVideoCreatorStore
  userIdentity: any;
  renderUsingChunks: boolean;
}

export const useVideoSelector = ({
  videoCreator,
  userIdentity,
  renderUsingChunks,
}: UseVideoSelectorProps) => {
  // State
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newVideoModal, setNewVideoModal] = useState<{
    type: 'save_as' | 'rename';
    video: DropdownOption | null;
  } | null>(null);
  const [actionProgress, setActionProgress] = useState<string>('');
  const [editingCurrentVideo, setEditingCurrentVideo] =
    useState<boolean>(false);
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string>('');
  const [optionsModal, setOptionsModal] = useState<{
    position: number;
    video: DropdownOption | null;
  } | null>(null);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownOpenRef = useRef<boolean>(false);
  const editingCurrentVideoRef = useRef<boolean>(false);

  // Helper function to get the most recent action date for a video
  const getMostRecentActionDate = (video: DropdownOption): number => {
    const allLastActions = [
      video.lastAction,
      ...(video.associatedVideos?.map(v => v.lastActionJson) || []),
    ].filter(Boolean) as VideoAction[];

    if (!allLastActions.length) return 0; // Videos without actions go to the end

    const mostRecent = allLastActions.reduce((latest, current) =>
      !latest || new Date(current.date) > new Date(latest.date)
        ? current
        : latest,
    );

    return new Date(mostRecent.date).getTime();
  };

  // Helper function to sort videos by most recent modification date
  const sortVideosByMostRecent = (
    videos: DropdownOption[],
  ): DropdownOption[] => {
    return videos.sort((a, b) => {
      const dateA = getMostRecentActionDate(a);
      const dateB = getMostRecentActionDate(b);
      return dateB - dateA; // Descending order (most recent first)
    });
  };

  // Prepare current video option
  const getCurrentVideoOption = (): DropdownOption | null => {
    const currentParentVideo =
      videoCreator.currentVideo?.parentVideo || videoCreator.currentVideo;
    return currentParentVideo
      ? ({
          id: currentParentVideo.id,
          title: currentParentVideo.title,
          duration:
            getFormattedDurationForStoryVideo(currentParentVideo).duration,
          videoFilePrimary: currentParentVideo.videoFilePrimary,
          associatedVideos: currentParentVideo.associatedVideos,
          aspectRatio: currentParentVideo.aspectRatio,
          isHidden: currentParentVideo.isHidden,
          lastAction: currentParentVideo.lastActionJson,
          isClientReady: currentParentVideo.isClientReady,
        } as DropdownOption)
      : null;
  };

  // Memoized video options data
  const allVideoOptions = useMemo(() => {
    const currentVideoOption = getCurrentVideoOption();

    const originalStoryOption = {
      id: 'original_story',
      title: videoCreator.story?.title,
      duration: videoCreator.story?.originalVideo?.video.duration,
    } as DropdownOption;

    let videoOptions: DropdownOption[] = [];
    if (currentVideoOption && !currentVideoOption.id) {
      videoOptions.push(currentVideoOption);
    }
    if (videoCreator.story?.otherVideos?.length) {
      videoOptions = videoOptions.concat(
        videoCreator.story?.otherVideos.map((video: any) => ({
          id: video.id!,
          title: video.title,
          duration: getFormattedDurationForStoryVideo(video).duration,
          videoFilePrimary: video.videoFilePrimary,
          associatedVideos: video.associatedVideos,
          aspectRatio: video.aspectRatio,
          isHidden: video.isHidden,
          lastAction: video.lastActionJson,
          isClientReady: video.isClientReady,
        })),
      );
    }

    videoOptions.push(originalStoryOption);

    // Sort videos by most recent modification date, but keep current video pinned at top
    const currentVideoId = currentVideoOption?.id;
    if (currentVideoId) {
      // Pin current video at top, sort the rest by most recent
      const currentVideo = videoOptions.find(
        video => video.id === currentVideoId,
      );
      const otherVideos = videoOptions.filter(
        video => video.id !== currentVideoId,
      );
      const sortedOtherVideos = sortVideosByMostRecent(otherVideos);
      videoOptions = currentVideo
        ? [currentVideo, ...sortedOtherVideos]
        : sortedOtherVideos;
    } else {
      // No current video, sort all by most recent
      videoOptions = sortVideosByMostRecent(videoOptions);
    }

    return videoOptions;
  }, [
    videoCreator.currentVideo?.id,
    videoCreator.currentVideo?.parentVideo?.id,
    videoCreator.story?.otherVideos,
    videoCreator.story?.title,
    videoCreator.story?.originalVideo?.video.duration,
  ]);

  // Mutating functions
  const copyVideo = async (video: DropdownOption, newTitle: string) => {
    if (video.id !== 'original_story') {
      await videoCreator.copyVideo(video.id, newTitle, 'creator-studio');
    } else {
      await videoCreator.createNewVideoFromSource(newTitle);
    }
    videoCreator.addUnsaveVideoAction();
  };

  const deleteVideo = async (videoId?: string) => {
    if (videoId === 'original_story') return;

    const idx = allVideoOptions.findIndex(data => data.id === videoId);
    let nextVideoIdx = idx + 1;
    if (nextVideoIdx === allVideoOptions.length - 1) {
      nextVideoIdx = idx - 1;
    }

    await videoCreator.removeStoryVideo(videoId);

    // Load the next video
    if (videoId === videoCreator.currentVideo!.id) {
      selectVideo(
        nextVideoIdx >= 0 ? allVideoOptions[nextVideoIdx].id : 'original_story',
      );
    }
    setOptionsModal(null);
  };

  const makeFeatureVideo = async (videoId: string) => {
    try {
      setOptionsModal(null);

      if (videoId === 'original_story') {
        await videoCreator.createNewVideoFromSource();
      } else if (videoId && videoId !== videoCreator.currentVideo?.id) {
        await videoCreator.loadVideo(videoId);
      }

      await videoCreator.saveStoryAndVideo(true);
      await videoCreator.publishVideo(VideoResolution.High, renderUsingChunks);
    } catch (error) {
      console.error('Error making feature video:', error);
    }
  };

  const selectVideo = (videoId: string) => {
    const currentVideoOption = allVideoOptions.find(
      v => v.id === videoCreator.currentVideo?.id,
    );

    if (videoId === currentVideoOption?.id) {
      return;
    } else if (videoId === 'original_story') {
      videoCreator.createNewVideoFromSource();
      videoCreator.addUnsaveVideoAction();
    } else {
      videoCreator.loadVideo(videoId);
    }
  };

  const handleRenameCurrentVideo = async () => {
    const currentVideoOption = allVideoOptions.find(
      v => v.id === videoCreator.currentVideo?.id,
    );
    const title = currentVideoTitle || '';
    if (title !== currentVideoOption?.title) {
      setActionProgress('Renaming video...');
      await videoCreator.renameVideo(currentVideoOption?.id!, title);
      setActionProgress('');
    }
  };

  // Event handlers
  const handleClick = () => {
    clickTimeoutRef.current = setTimeout(() => {
      dropdownOpenRef.current = true;
      if (!editingCurrentVideoRef.current) setDropdownOpen(open => !open);
    }, 200);
  };

  const handleShowEditInput = () => {
    editingCurrentVideoRef.current = true;
    setEditingCurrentVideo(true);
    setDropdownOpen(false);
  };

  const toggleEditingInput = () => {
    if (isMouseDown) return;
    setEditingCurrentVideo(false);
    editingCurrentVideoRef.current = false;
  };

  const handleDoubleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    handleShowEditInput();
  };

  // Effects
  useEffect(() => {
    if (!editingCurrentVideoRef.current) return;
    const currentVideoOption = allVideoOptions.find(
      v => v.id === videoCreator.currentVideo?.id,
    );
    setCurrentVideoTitle(currentVideoOption?.title || '');
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(
      inputRef.current.value.length,
      inputRef.current.value.length,
    );
  }, [editingCurrentVideoRef.current, inputRef.current, allVideoOptions]);

  const handleToggleEditingVideo = async (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      if (!isMouseDown) toggleEditingInput();
    }
  };

  const handleMouseDown = () => {
    setIsMouseDown(true);
  };

  const handleMouseUp = (event: MouseEvent) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target as Node) &&
      !window.getSelection()?.toString()
    ) {
      setIsMouseDown(false);
    }
  };

  useEffect(() => {
    window.addEventListener('click', handleToggleEditingVideo);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('click', handleToggleEditingVideo);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown]);

  return {
    // State
    dropdownOpen,
    setDropdownOpen,
    newVideoModal,
    setNewVideoModal,
    actionProgress,
    setActionProgress,
    editingCurrentVideo,
    setEditingCurrentVideo,
    currentVideoTitle,
    setCurrentVideoTitle,
    optionsModal,
    setOptionsModal,
    isMouseDown,
    setIsMouseDown,

    // Refs
    inputRef,
    containerRef,
    buttonRef,
    clickTimeoutRef,
    dropdownOpenRef,
    editingCurrentVideoRef,

    // Data
    allVideoOptions,
    currentVideoOption: getCurrentVideoOption(),
    getMostRecentActionDate,

    // Actions
    copyVideo,
    deleteVideo,
    makeFeatureVideo,
    selectVideo,
    handleRenameCurrentVideo,

    // Event handlers
    handleClick,
    handleShowEditInput,
    toggleEditingInput,
    handleDoubleClick,
  };
};
